/*
 * A small typed fetch client for /api. Every failure becomes an ApiError
 * with a message that is safe to show as-is.
 */
import { brandName } from "../lib/brand";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string>;

  constructor(status: number, code: string, message: string, fields: Record<string, string> = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
  }

  get isNetwork() {
    return this.status === 0 || this.code === "unavailable";
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

type Method = "GET" | "POST" | "DELETE" | "PATCH" | "PUT";

const FALLBACK: Record<number, string> = {
  400: "Something in the request wasn't right. Check the form and try again.",
  401: "You're signed out. Sign in again to continue.",
  403: "You don't have permission to do that.",
  404: "We couldn't find that. It may have been deleted, or you may not have access.",
  409: "That conflicts with something that already exists.",
  429: "Too many attempts. Wait a minute and try again.",
};

async function request<T>(method: Method, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const init: RequestInit = { method, credentials: "include", headers };
  if (signal) init.signal = signal;
  // The API requires application/json on every state-changing request, even
  // ones without a payload, so those always send at least {}.
  if (method !== "GET") {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body ?? {});
  }

  let res: Response;
  try {
    res = await fetch(`/api${path}`, init);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(0, "network", `We can't reach ${brandName()}. Check your connection and try again.`);
  }

  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }

  if (!res.ok) {
    const e = (json as { error?: { code?: string; message?: string; fields?: Record<string, string> } } | null)?.error;
    if (e && typeof e.message === "string") {
      throw new ApiError(res.status, e.code ?? "error", e.message, e.fields ?? {});
    }
    if (res.status >= 500) {
      // An empty 5xx is what the dev proxy sends when the API isn't running.
      const down = json === null || res.status === 502 || res.status === 503 || res.status === 504;
      throw new ApiError(
        res.status,
        down ? "unavailable" : "server",
        down ? `${brandName()} isn't responding. Try again in a moment.` : "Something went wrong on our side. Try again in a moment.",
      );
    }
    throw new ApiError(res.status, "error", FALLBACK[res.status] ?? "Something went wrong. Try again in a moment.");
  }

  if (json === null && text) {
    throw new ApiError(res.status, "unavailable", `${brandName()} sent a response we couldn't read. Try again in a moment.`);
  }
  return json as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>("GET", path, undefined, signal),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string, body?: unknown) => request<T>("DELETE", path, body),
};

/** Build a query string, leaving out empty values. */
export function qs(params: Record<string, string | number | null | undefined>) {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") s.set(k, String(v));
  }
  const out = s.toString();
  return out ? `?${out}` : "";
}
